import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Star, Home, Building2, Warehouse, Building, Briefcase, Bed, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROPERTY_TYPES = [
  { value: 'home', label: 'House', icon: Home },
  { value: 'apartment', label: 'Apartment', icon: Bed },
  { value: 'condo', label: 'Condo', icon: Building },
  { value: 'business', label: 'Business', icon: Briefcase },
  { value: 'office', label: 'Office', icon: Building2 },
  { value: 'warehouse', label: 'Warehouse', icon: Warehouse },
  { value: 'other', label: 'Other', icon: Building },
];

const propertyIcon = (type) => {
  const entry = PROPERTY_TYPES.find((p) => p.value === type);
  return entry ? entry.icon : Home;
};

const propertyLabel = (type) => {
  const entry = PROPERTY_TYPES.find((p) => p.value === type);
  return entry ? entry.label : type || 'Place';
};

const emptyForm = {
  label: '',
  property_type: 'home',
  street_address: '',
  unit: '',
  city: '',
  state: '',
  zip_code: '',
  notes: '',
  is_default: false,
};

/**
 * Customer Places Manager — full CRUD with property type selection.
 * onSelect (optional): callback fired when user picks a place (used in booking flow).
 * selectedId (optional): currently selected place id (used in booking flow).
 * mode: "manage" (default, full CRUD ui) | "picker" (compact list with select buttons)
 */
const PlacesManager = ({ onSelect, selectedId, mode = 'manage' }) => {
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // place object or 'new'
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('token');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/places`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlaces(r.data || []);
    } catch (e) {
      console.error('Failed to load places', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm(emptyForm);
    setEditing('new');
    setError(null);
  };

  const openEdit = (p) => {
    setForm({
      label: p.label || '',
      property_type: p.property_type || 'home',
      street_address: p.street_address || '',
      unit: p.unit || '',
      city: p.city || '',
      state: p.state || '',
      zip_code: p.zip_code || '',
      notes: p.notes || '',
      is_default: !!p.is_default,
    });
    setEditing(p);
    setError(null);
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const payload = { ...form, unit: form.unit || null, notes: form.notes || null };
      if (editing === 'new') {
        await axios.post(`${API}/places`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.put(`${API}/places/${editing.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setEditing(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save place');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (place) => {
    if (!window.confirm(`Delete "${place.label}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/places/${place.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not delete place');
    }
  };

  const setDefault = async (place) => {
    try {
      await axios.put(
        `${API}/places/${place.id}`,
        { is_default: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not update default');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-text-secondary py-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading places…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!editing && (
        <>
          {places.length === 0 ? (
            <div
              className="bg-surface-muted border border-dashed border-border rounded-2xl p-8 text-center"
              data-testid="places-empty-state"
            >
              <Home className="w-10 h-10 mx-auto mb-3 text-text-secondary" />
              <h3 className="font-heading text-lg font-semibold text-text-primary mb-1">
                No saved places yet
              </h3>
              <p className="text-sm text-text-secondary mb-4">
                Add the address(es) where you&apos;d like cleanings. You can save multiple — like home, office, warehouse, etc.
              </p>
              <button
                type="button"
                onClick={openNew}
                className="btn-primary text-sm inline-flex items-center gap-2"
                data-testid="add-first-place-button"
              >
                <Plus className="w-4 h-4" />
                Add your first place
              </button>
            </div>
          ) : (
            <>
              <div className="space-y-3" data-testid="places-list">
                {places.map((p) => {
                  const Icon = propertyIcon(p.property_type);
                  const isSelected = selectedId === p.id;
                  return (
                    <div
                      key={p.id}
                      className={`bg-surface border rounded-2xl p-4 transition-all ${
                        isSelected
                          ? 'border-primary ring-2 ring-primary ring-offset-2'
                          : 'border-border hover:border-primary/40'
                      }`}
                      data-testid={`place-card-${p.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="bg-primary-light text-primary p-2 rounded-xl shrink-0">
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h4
                              className="font-semibold text-text-primary truncate"
                              data-testid={`place-label-${p.id}`}
                            >
                              {p.label}
                            </h4>
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-surface-muted text-text-secondary rounded-full">
                              {propertyLabel(p.property_type)}
                            </span>
                            {p.is_default && (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-primary text-white rounded-full"
                                data-testid={`place-default-${p.id}`}
                              >
                                <Star className="w-3 h-3 fill-white" />
                                Default
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-text-secondary leading-tight">
                            {p.street_address}
                            {p.unit ? `, ${p.unit}` : ''}
                          </p>
                          <p className="text-sm text-text-secondary leading-tight">
                            {p.city}, {p.state} {p.zip_code}
                          </p>
                          {p.notes && (
                            <p className="text-xs text-text-secondary italic mt-1">
                              Note: {p.notes}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          {mode === 'picker' && onSelect && (
                            <button
                              type="button"
                              onClick={() => onSelect(p)}
                              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
                                isSelected
                                  ? 'bg-primary text-white'
                                  : 'bg-surface-muted text-text-primary hover:bg-primary-light'
                              }`}
                              data-testid={`select-place-${p.id}`}
                            >
                              {isSelected ? 'Selected' : 'Use this'}
                            </button>
                          )}
                          {mode === 'manage' && (
                            <>
                              {!p.is_default && (
                                <button
                                  type="button"
                                  onClick={() => setDefault(p)}
                                  className="text-xs text-text-secondary hover:text-primary px-2 py-1 inline-flex items-center gap-1"
                                  data-testid={`make-default-${p.id}`}
                                  title="Make this my default"
                                >
                                  <Star className="w-3 h-3" />
                                  Default
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => openEdit(p)}
                                className="text-xs text-text-secondary hover:text-primary px-2 py-1 inline-flex items-center gap-1"
                                data-testid={`edit-place-${p.id}`}
                              >
                                <Pencil className="w-3 h-3" />
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => remove(p)}
                                className="text-xs text-text-secondary hover:text-red-500 px-2 py-1 inline-flex items-center gap-1"
                                data-testid={`delete-place-${p.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={openNew}
                className="w-full bg-surface border border-dashed border-border hover:border-primary rounded-2xl py-3 inline-flex items-center justify-center gap-2 text-sm text-text-secondary hover:text-primary transition-colors"
                data-testid="add-place-button"
              >
                <Plus className="w-4 h-4" />
                Add another place
              </button>
            </>
          )}
        </>
      )}

      {editing && (
        <form
          onSubmit={(e) => { e.preventDefault(); submit(); }}
          className="bg-surface border border-border rounded-2xl p-5 space-y-4"
          data-testid="place-form"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-lg font-semibold text-text-primary">
              {editing === 'new' ? 'Add a place' : 'Edit place'}
            </h3>
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="text-sm text-text-secondary hover:text-primary"
              data-testid="cancel-place-button"
            >
              Cancel
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3" data-testid="place-form-error">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">Property type</label>
            <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
              {PROPERTY_TYPES.map((opt) => {
                const Icon = opt.icon;
                const active = form.property_type === opt.value;
                return (
                  <button
                    type="button"
                    key={opt.value}
                    onClick={() => setForm({ ...form, property_type: opt.value })}
                    className={`flex flex-col items-center gap-1 px-2 py-3 rounded-xl border text-xs font-medium transition-all ${
                      active
                        ? 'bg-primary text-white border-primary'
                        : 'bg-surface-muted text-text-secondary border-border hover:border-primary'
                    }`}
                    data-testid={`property-type-${opt.value}`}
                  >
                    <Icon className="w-4 h-4" />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">Label (so you can tell places apart)</label>
            <input
              type="text"
              required
              maxLength={60}
              placeholder="e.g. My House, Downtown Office, Warehouse #3"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              className="input-field"
              data-testid="place-label-input"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-text-secondary mb-1">Street address</label>
              <input
                type="text"
                required
                maxLength={200}
                placeholder="123 Main St"
                value={form.street_address}
                onChange={(e) => setForm({ ...form, street_address: e.target.value })}
                className="input-field"
                data-testid="place-street-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Apt / Suite / Unit</label>
              <input
                type="text"
                maxLength={40}
                placeholder="(optional)"
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                className="input-field"
                data-testid="place-unit-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-text-secondary mb-1">City</label>
              <input
                type="text"
                required
                maxLength={80}
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="input-field"
                data-testid="place-city-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">State</label>
              <input
                type="text"
                required
                maxLength={40}
                placeholder="NY"
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })}
                className="input-field"
                data-testid="place-state-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">ZIP</label>
              <input
                type="text"
                required
                placeholder="10001"
                value={form.zip_code}
                onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
                className="input-field"
                data-testid="place-zip-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">Access notes (optional)</label>
            <textarea
              maxLength={500}
              rows={2}
              placeholder="Gate code, parking instructions, pet warnings, etc."
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="input-field"
              data-testid="place-notes-input"
            />
          </div>

          <label className="inline-flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_default}
              onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
              className="w-4 h-4"
              data-testid="place-default-toggle"
            />
            Make this my default place
          </label>

          <button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full inline-flex items-center justify-center gap-2"
            data-testid="save-place-button"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {editing === 'new' ? 'Save place' : 'Save changes'}
          </button>
        </form>
      )}
    </div>
  );
};

export default PlacesManager;
